import { useEffect, useMemo, useRef, useState } from "react";
import {
  formProfileDefinition,
  type FormProfileCatalog,
  type FormProfileManage,
  type Project,
  type Values,
} from "@proof/core";
import { ProfileGrid, type ProfileGridRow } from "@proof/authoring";
import { FormGenerator } from "@proof/ui";
import { Button, Control, Modal } from "./components";
import { downloadJson } from "./api";
import { useWorkspaceTransport } from "./WorkspaceContext";

type Collection = FormProfileCatalog["collections"][number];
type Profile = Collection["profiles"][number];
/** Where a saved profile's form lives, so its values can be edited by type. */
function profileForm(
  projects: Project[],
  source: Collection["source"],
): { project: Project; formId: string } | undefined {
  if (source.kind === "document") {
    const project = projects.find((row) => row.id === source.projectId);
    return project?.forms.some((form) => form.id === source.formId)
      ? { project, formId: source.formId }
      : undefined;
  }
  for (const project of projects) {
    const form = project.forms.find(
      (row) => row.profileLibrary?.id === source.libraryResourceId,
    );
    if (form) return { project, formId: form.id };
  }
}
export function ProfileCollections({
  projects = [],
}: {
  /** Projects the account can open; used to edit values by profile type. */
  projects?: Project[];
} = {}) {
  const transport = useWorkspaceTransport(),
    { client, demo } = transport;
  const [loaded, setLoaded] = useState<{
      client: typeof client;
      page: FormProfileCatalog;
    }>(),
    [cursor, setCursor] = useState(""),
    [previous, setPrevious] = useState<string[]>([]),
    [refresh, setRefresh] = useState(0),
    [search, setSearch] = useState(""),
    [selected, setSelected] = useState<{
      collectionId: string;
      profile: Profile;
    }>(),
    [name, setName] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [values, setValues] = useState<Values>(),
    [valuesError, setValuesError] = useState("");
  const session = useRef({ client });
  if (session.current.client !== client) session.current = { client };
  const generation = useRef(0),
    mounted = useRef(true);
  const page = loaded?.client === client ? loaded.page : undefined;
  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController(),
      request = ++generation.current;
    setBusy(true);
    setError("");
    void client
      .formProfileCatalog({ ...(cursor ? { cursor } : {}) }, controller.signal)
      .then((page) => {
        if (!controller.signal.aborted && generation.current === request)
          setLoaded({ client, page });
      })
      .catch((error) => {
        if (!controller.signal.aborted && generation.current === request)
          setError(error.message);
      })
      .finally(() => {
        if (!controller.signal.aborted && generation.current === request)
          setBusy(false);
      });
    return () => {
      mounted.current = false;
      controller.abort();
    };
  }, [client, cursor, refresh]);
  useEffect(() => {
    setCursor("");
    setPrevious([]);
    setSelected(undefined);
    setName("");
    setSearch("");
    setNotice("");
  }, [client]);
  const perform = async (
    action: "save" | "revoke" | "delete" | "export" | "remove",
    recipient?: string,
  ) => {
    if (!selected || busy) return;
    const currentSession = session.current,
      request = generation.current,
      active = () =>
        mounted.current &&
        currentSession === session.current &&
        request === generation.current;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (action === "export") {
        const data = await client.ownedFormProfile(
          selected.collectionId,
          selected.profile.id,
        );
        if (active()) {
          downloadJson(data, `profile-${data.profile.id}.json`);
          setNotice("Profile data downloaded.");
        }
      } else {
        if (action === "delete") {
          await client.removeOwnedFormProfile(
            selected.collectionId,
            selected.profile.id,
            selected.profile.revision,
          );
          if (active()) {
            setSelected(undefined);
            setName("");
            setNotice("Profile deleted. Its sharing has been removed.");
          }
        } else {
          const input: FormProfileManage = {
            expectedRevision: selected.profile.revision,
            ...(action === "save"
              ? { name }
              : action === "revoke"
                ? { revokeSharing: true as const }
                : { removeRecipients: [recipient!] }),
          };
          const profile = await client.manageFormProfile(
            selected.collectionId,
            selected.profile.id,
            input,
          );
          if (active()) {
            setSelected({ ...selected, profile });
            if (action === "save") setName(profile.name);
            setNotice(
              action === "save" ? "Profile renamed." : "Sharing updated.",
            );
          }
        }
        if (active()) {
          const next = await client.formProfileCatalog({
            ...(cursor ? { cursor } : {}),
          });
          if (active()) setLoaded({ client, page: next });
        }
      }
    } catch (error) {
      if (active()) setError((error as Error).message);
    } finally {
      if (active()) setBusy(false);
    }
  };
  const choose = (collection: Collection, profile: Profile) => {
    setSelected({ collectionId: collection.id, profile });
    setName(profile.name);
    setError("");
    setNotice("");
    setValues(undefined);
    setValuesError("");
    // Values load when the profile is opened, so they are always current.
    void client
      .ownedFormProfile(collection.id, profile.id)
      .then((data) => setValues(data.profile.values))
      .catch((error) => setValuesError((error as Error).message));
  };
  const selectedCollection = page?.collections.find(
    (collection) => collection.id === selected?.collectionId,
  );
  const target = selectedCollection
    ? profileForm(projects, selectedCollection.source)
    : undefined;
  const definition = useMemo(() => {
    if (!target) return undefined;
    try {
      return formProfileDefinition(target.project, target.formId);
    } catch {
      return undefined;
    }
  }, [target?.project, target?.formId]);
  const typeName = (collection: Collection) => {
    const found = profileForm(projects, collection.source);
    const form = found?.project.forms.find((row) => row.id === found.formId);
    return (
      form?.name ||
      (collection.source.kind === "library"
        ? "Reusable form profile"
        : "Document form profile")
    );
  };
  const rows: ProfileGridRow[] = (page?.collections || []).flatMap(
    (collection) =>
      collection.profiles.map((profile) => ({
        id: profile.id,
        name: profile.name,
        typeId: collection.id,
        typeName: typeName(collection),
        status: profile.recipients.length
          ? `Shared with ${profile.recipients.length}`
          : "Private",
        updatedAt: profile.updatedAt,
      })),
  );
  const typeOptions = [
    ...new Map(
      (page?.collections || []).map((collection) => [
        typeName(collection),
        collection.id,
      ]),
    ),
  ].map(([name, id]) => ({ id, name }));
  const saveValues = async () => {
    if (!selected || !values || !target || !selectedCollection || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const profile = await client.updateFormProfile(
        {
          ...transport.projectInput(target.project),
          formId: target.formId,
          collection: selectedCollection.source.kind,
        },
        selected.profile.id,
        {
          name: name.trim() || selected.profile.name,
          values,
          expectedRevision: selected.profile.revision,
        },
      );
      setValues(profile.values);
      setNotice("Profile saved.");
      const next = await client.formProfileCatalog({
        ...(cursor ? { cursor } : {}),
      });
      setLoaded({ client, page: next });
      const summary = next.collections
        .find((collection) => collection.id === selected.collectionId)
        ?.profiles.find((row) => row.id === selected.profile.id);
      if (summary) setSelected({ ...selected, profile: summary });
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setBusy(false);
    }
  };
  void search;
  return (
    <section
      className="profile-collections"
      aria-label="Your saved form profiles"
    >
      <header className="page-intro">
        <div>
          <div className="eyebrow">YOUR ACCOUNT</div>
          <h1>Your profiles</h1>
          <p>
            Profiles saved to your account. Open one to edit its values with the
            same inputs as its form.
          </p>
          {demo && (
            <p className="muted">
              These profiles use the shared demo account on this API.
            </p>
          )}
        </div>
      </header>
      {error && !selected && <p role="alert">{error}</p>}
      {notice && !selected && <p role="status">{notice}</p>}
      {busy && !page && <p role="status">Loading profiles…</p>}
      {page && (
        <>
          {!page.durable && (
            <p role="status">
              This server uses memory storage. Profiles last until the server
              restarts.
            </p>
          )}
          <ProfileGrid
            label="Your profiles"
            rows={rows}
            types={typeOptions}
            empty="You haven't saved any profiles yet. Save one from a form in Live proof."
            onOpen={(row) => {
              const collection = page.collections.find(
                (item) => item.id === row.typeId,
              );
              const profile = collection?.profiles.find(
                (item) => item.id === row.id,
              );
              if (collection && profile) choose(collection, profile);
            }}
          />
          {(previous.length > 0 || page.nextCursor) && (
            <div className="button-row">
              <Button
                disabled={busy || !previous.length}
                onClick={() => {
                  setCursor(previous.at(-1)!);
                  setPrevious((stack) => stack.slice(0, -1));
                }}
              >
                Previous profile page
              </Button>
              <Button
                disabled={busy || !page.nextCursor}
                onClick={() => {
                  setPrevious((stack) => [...stack, cursor]);
                  setCursor(page.nextCursor!);
                }}
              >
                Next profile page
              </Button>
            </div>
          )}
        </>
      )}
      {selected && (
        <Modal
          title={`Edit ${selected.profile.name}`}
          wide
          onClose={() => {
            if (!busy) {
              setSelected(undefined);
              setRefresh((value) => value + 1);
            }
          }}
        >
          <fieldset
            disabled={busy}
            style={{ border: 0, padding: 0, minWidth: 0 }}
            aria-label="Selected profile management"
          >
            {error && <p role="alert">{error}</p>}
            {notice && <p role="status">{notice}</p>}
            <Control label="Saved profile name">
              <input
                value={name}
                maxLength={100}
                onChange={(event) => setName(event.target.value)}
              />
            </Control>
            <h3>Profile values</h3>
            {valuesError && <p role="alert">{valuesError}</p>}
            {!values && !valuesError && (
              <p role="status">Loading profile values…</p>
            )}
            {values && definition ? (
              <FormGenerator
                project={definition}
                values={values}
                onChange={setValues}
                loadAsset={transport.loadAsset}
                transformImage={transport.proofServices.transformImage}
              />
            ) : (
              values && (
                <p className="muted">
                  The form this profile came from isn't open in this workspace,
                  so its values can be downloaded but not edited here.
                </p>
              )
            )}
            <div className="button-row">
              {definition && values ? (
                <Button kind="primary" onClick={() => void saveValues()}>
                  Save profile
                </Button>
              ) : (
                <Button
                  disabled={!name.trim() || name === selected.profile.name}
                  onClick={() => void perform("save")}
                >
                  Save profile name
                </Button>
              )}
              <Button onClick={() => void perform("export")}>
                Download profile data
              </Button>
            </div>
            <details>
              <summary>Sharing & delete</summary>
              {!selected.profile.recipients.length ? (
                <p>This profile is private.</p>
              ) : (
                <>
                  <ul className="profile-recipients">
                    {selected.profile.recipients.map((recipient) => (
                      <li key={recipient}>
                        <span>{recipient}</span>
                        <button
                          className="button"
                          type="button"
                          aria-label={`Remove recipient ${recipient}`}
                          onClick={() => void perform("remove", recipient)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => void perform("revoke")}>
                    Stop sharing
                  </Button>
                </>
              )}
              <p>
                Deleting removes this profile's values and sharing. Saved proofs
                and personal copies remain.
              </p>
              <Button onClick={() => void perform("delete")}>
                Delete profile
              </Button>
            </details>
          </fieldset>
        </Modal>
      )}
    </section>
  );
}
